// Helpers for forms generated from the stored structure. No field names live here.

// Replace every `arc` field by a group holding arcFields (mirrors the server).
export function resolveFields(fields, arcFields) {
  return (fields || []).map((f) => {
    if (f.type === "arc") return { ...f, type: "group", fields: arcFields || [], _arc: true };
    if (f.type === "group") return { ...f, fields: resolveFields(f.fields, arcFields) };
    return f;
  });
}

export const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

export function deepMerge(base, over) {
  if (isObj(base) && isObj(over)) {
    const out = { ...base };
    Object.keys(over).forEach((k) => { out[k] = k in out ? deepMerge(out[k], over[k]) : over[k]; });
    return out;
  }
  return over === undefined ? base : over;
}

export function setIn(obj, path, value) {
  if (path.length === 0) return value;
  const [h, ...rest] = path;
  return { ...(obj || {}), [h]: setIn((obj || {})[h], rest, value) };
}

export function optionValues(field, patientData) {
  let opts = field.options || [];
  if (typeof opts === "string") {            // key of the patient structure, e.g. "reachZones" -> cells
    const node = patientData?.[opts];
    opts = (node && node.cells) || node || [];
  }
  if (!opts.length && field.optionsFrom) {
    opts = String(field.optionsFrom).split(".").reduce((n, k) => (n ? n[k] : undefined), patientData) || [];
  }
  return opts.map((o) => (isObj(o) ? o.value : o));
}

export function defaultsFor(fields, patientData) {
  const out = {};
  (fields || []).forEach((f) => {
    if (f.type === "group") out[f.key] = defaultsFor(f.fields, patientData);
    else if ("default" in f) out[f.key] = f.default;
    else if (f.type === "bool") out[f.key] = false;
    else if (f.type === "int" || f.type === "float") out[f.key] = f.min ?? 0;
    else if (f.type === "json") out[f.key] = "{}";
    else if (f.type === "enum") out[f.key] = optionValues(f, patientData)[0] ?? "";
    else out[f.key] = "";
  });
  return out;
}

// Zones string <-> list, in schema cell order
export function parseZones(value, cells) {
  const v = (value || "").trim();
  if (v === "All") return [...cells];
  if (v === "" || v === "None") return [];
  const set = new Set(v.split(",").map((x) => x.trim()));
  return cells.filter((c) => set.has(c));
}

export function joinZones(list, cells) {
  const set = new Set(list);
  const chosen = cells.filter((c) => set.has(c));
  if (chosen.length === 0) return "None";
  if (chosen.length === cells.length) return "All";
  return chosen.join(", ");
}

export function fmtMeta(meta, names) {
  if (!meta || !meta.Version) return "";
  const when = meta.UpdatedAt ? new Date(meta.UpdatedAt).toISOString().slice(0, 10) : "";
  const by = meta.UpdatedBy ? (names?.[meta.UpdatedBy] || (meta.UpdatedBy === "system" ? "system" : "")) : "";
  return `v${meta.Version}, updated ${when}${by ? ` by ${by}` : ""}`;
}

export function downloadJson(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(url);
}

// "2:30" / "4 min" / "30 sec" / 90 -> seconds (null when it is not a time)
export function parseSeconds(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return v;
  const s = String(v).trim().toLowerCase();
  let m = s.match(/^(\d+):(\d{2})$/);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  m = s.match(/^(\d+(?:\.\d+)?)\s*(min|sec|s)\b/);
  if (m) return m[2] === "min" ? Number(m[1]) * 60 : Number(m[1]);
  return null;
}

// Minutes one session of an activity takes at a level, from its difficulty guide (a patient's overrides win):
// session length, else rounds x round time + rests, else time limit. null when the guide has no timing.
export function estimateMinutes(module, level, overrides = {}) {
  const rows = module?.difficultyGuide || [];
  const find = (re) => rows.find((r) => re.test(r.label));
  const raw = (r) => (r && overrides[r.key] !== undefined && overrides[r.key] !== "" ? overrides[r.key] : r?.values?.[level - 1]);
  const sec = (r) => (r ? parseSeconds(raw(r)) : null);
  const one = (x) => Math.round(x * 10) / 10;
  const session = sec(find(/^session length/i));
  if (session) return one(session / 60);
  const roundTime = sec(find(/^round time/i));
  const rounds = Number(raw(find(/^rounds$/i)));
  const rest = sec(find(/^rest/i)) || 0;
  if (roundTime && Number.isFinite(rounds) && rounds > 0) return one((rounds * roundTime + (rounds - 1) * rest) / 60);
  const limit = sec(find(/^time limit/i));
  return limit ? one(limit / 60) : null;
}

export async function downloadCsv(api, params, filename = "analytics.csv") {
  const r = await api.get("/telemetry/export", { params, responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(url);
}

export const pretty = (v) => (v === undefined ? "" : typeof v === "string" ? v : JSON.stringify(v));
